// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Pagination, { PaginationPresentationProps } from "@conklin/MobiusOverrides/Pagination/Pagination";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Changes: Add new Fields
const enum fields {
    showResultsPerPage = "showResultsPerPage",
    showPagesNumber = "showPagesNumber",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showResultsPerPage]: boolean;
        [fields.showPagesNumber]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistoryPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const orderHistoryPaginationStyles: OrderHistoryPaginationStyles = {};

const styles = orderHistoryPaginationStyles;

const OrderHistoryPagination = ({
    isVmiOrderHistoryPage,
    updateSearchFields,
    fields: { showResultsPerPage, showPagesNumber },
}: Props) => {
    const ordersDataView = useContext(OrdersDataViewContext);
    const pagination = ordersDataView.value && ordersDataView.pagination ? ordersDataView.pagination : undefined;

    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    if (!pagination) {
        return null;
    }

    const { totalItemCount, page, pageSize, pageSizeOptions, prevPageUri, nextPageUri } = pagination;
    if (totalItemCount === 0) {
        return null;
    }

    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    const pageSizeCookieName = isVmiOrderHistoryPage ? "VmiOrderHistory-PageSize" : "OrderHistory-PageSize";

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={totalItemCount}
            currentPage={page}
            resultsPerPage={pageSize}
            resultsPerPageOptions={pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie={pageSizeCookieName}
            prevPage={prevPageUri}
            nextPage={nextPageUri}
            // XNG-Changes
            showResultsPerPage={showResultsPerPage}
            showPagesNumber={showPagesNumber}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistoryPagination),
    definition: {
        displayName: "Pagination",
        group: "Order History",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showResultsPerPage,
                displayName: "Show Results Per Page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPagesNumber,
                displayName: "Show Pages Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
